// TaskDeliverables — GPX-only section that lists the documents a GP can
// view/download for the active task. Filtering by isExternal happens upstream
// in WorkflowDetail; this component only renders what it's handed.

import React from "react";
import { Flex, Link, Text, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useDocumentDownloadUrlLazyQuery } from "#graphql/supergraphHooks";
import { SPACING } from "#utils/spacing";
import { SubtaskItemType, type SubtaskItem } from "../types";
import * as styles from "./styles_vanilla.css";
import type { TaskDocumentAttachment } from "./types";

const DeliverableRow: React.FC<{ attachment: TaskDocumentAttachment }> = ({ attachment }) => {
  const doc = attachment.document;
  const [fetchUrl, { loading }] = useDocumentDownloadUrlLazyQuery();

  if (!doc) return null;

  const handleDownload = async () => {
    const { data } = await fetchUrl({ variables: { id: doc.id } });
    const url = data?.documentDownloadUrl;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      toast.error("Download link is unavailable. Please try again later.");
    }
  };

  return (
    <Flex
      alignItems="center"
      gap={SPACING.MEDIUM}
      className={styles.deliverableRow}
      data-testid={`deliverable-row-${doc.id}`}
    >
      <div className={styles.deliverableFileIcon}>
        <Icon type="file-alt" color={Colors.Neutral600} size="medium" variant="solid" />
      </div>
      <Flex flexDirection="column" gap="2px" className={styles.deliverableMeta}>
        <Text variant="regular-sm" className={styles.deliverableFileName}>
          {doc.fileName}
        </Text>
      </Flex>
      <button
        type="button"
        className={styles.deliverableDownloadButton}
        onClick={() => {
          void handleDownload();
        }}
        disabled={loading}
        aria-label={`Download ${doc.fileName}`}
        data-testid={`deliverable-download-${doc.id}`}
      >
        <Icon type="download" size="small" />
      </button>
    </Flex>
  );
};

const SubtaskUrlRow: React.FC<{ subtask: SubtaskItem }> = ({ subtask }) => {
  const url = typeof subtask.value === "string" ? subtask.value.trim() : "";
  if (!url) return null;

  return (
    <Flex
      flexDirection="column"
      gap="2px"
      alignItems="flex-start"
      className={styles.deliverableRow}
      data-testid={`deliverable-url-row-${subtask.id}`}
    >
      <Text variant="regular-md" className={styles.deliverableFileName}>
        {subtask.label}
      </Text>
      <Link
        href={url}
        shouldOpenInNewTab
        aria-label={`Open ${subtask.label}`}
        data-testid={`deliverable-url-link-${subtask.id}`}
      >
        <Text
          variant="regular-sm"
          color={Colors.Neutral600}
          className={styles.deliverableUrlSubtext}
        >
          {url}
        </Text>
      </Link>
    </Flex>
  );
};

const isUrlDeliverableSubtask = (subtask: SubtaskItem): boolean =>
  subtask.itemType === SubtaskItemType.INPUT_FIELD &&
  subtask.isCompleted === true &&
  (subtask.config as { input_type?: string } | undefined)?.input_type === "url";

type TaskDeliverablesProps = {
  deliverables: TaskDocumentAttachment[];
  subtasks?: SubtaskItem[];
};

export const TaskDeliverables: React.FC<TaskDeliverablesProps> = ({
  deliverables,
  subtasks = [],
}) => {
  const urlSubtasks = subtasks.filter(isUrlDeliverableSubtask);
  const isEmpty = deliverables.length === 0 && urlSubtasks.length === 0;

  return (
    <Flex
      flexDirection="column"
      gap={SPACING.SMALL}
      className={styles.deliverableSection}
      data-testid="task-deliverables"
    >
      <Text variant="h3">Deliverables</Text>
      {isEmpty ? (
        <Text
          variant="regular-md"
          color={Colors.Neutral600}
          className={styles.deliverableEmptyMessage}
          data-testid="task-deliverables-empty"
        >
          This task has no deliverables for you to review
        </Text>
      ) : (
        <Flex flexDirection="column" className={styles.deliverableList}>
          {deliverables.map((attachment) => (
            <DeliverableRow key={attachment.id} attachment={attachment} />
          ))}
          {urlSubtasks.map((subtask) => (
            <SubtaskUrlRow key={`url-${subtask.id}`} subtask={subtask} />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default TaskDeliverables;
